/**
 * The Comparison tab's section figures, as one pure function every surface
 * reads: the Comparison table, the report screen (and so every PDF captured from
 * it) and the server's email/report context.
 *
 * Erin, NMI 60014106913: "the comparisons are focusing on the bill's categories,
 * not our categories ... the report should reflect the comparison. It does not
 * need to take the bill into account at all." The report used to read
 * `section_totals` straight off the stored results. On the invoice side that is
 * whichever heading the retailer printed a charge under, so a daily charge we
 * file under Metering showed as a Retail overcharge plus an equal Metering
 * undercharge, and other industry charges printed under "Network and other
 * charges" showed up as a Network variance nobody could find on the Comparison
 * tab. Going through the charge map, the pairing and the canonical sections here
 * means the report has exactly the sections the Comparison tab has.
 *
 * Pure and sync: the charge map rows are passed in, the same way `pairLines`
 * takes them, so the server and the browser get the same answer from the same
 * code.
 */
import type {
  ActualBillResult, CalcExclusion, ShadowBillResult, ShadowBillSection,
} from '../types/billTypes';
import type { ChargeMapRow } from '../utils/chargeMap';
import type { NameAliases } from '../utils/nameAliases';
import {
  actualSectionFor, applyCanonicalSections, comparisonTotals, excludedFromVariance,
  isRowExcluded, itemsInSeveralSections, pairLines,
  type ChargeRow, type ComparableLine, type ComparisonTotals, type MapContext,
} from '../utils/comparisonRows';
import { REPORT_SECTIONS } from './reportFormulas';

/** The sections a charge can be compared in, in display order. GST has no rows. */
export const COMPARED_SECTIONS: ShadowBillSection[] = [
  'retail', 'distribution', 'metering', 'environmental', 'other',
];

/**
 * The `bv_retailer_charge_map` columns a `ChargeMapRow` is read from. Shared so
 * the browser and the server select the same shape.
 *
 * bill_section_canonical, not bill_section: the raw retailer wording is seeding
 * provenance only, never read at runtime (see chargeMap.ts). The generated
 * columns distributor_key/tariff_key/bill_item_key exist purely so the seed's
 * upsert can name them in onConflict, so they are left out.
 */
export const CHARGE_MAP_COLUMNS =
  'distributor,tariff_code,bill_section_canonical,bill_item,expected_item,'
  + 'expected_section,demand_unit,group_key,map_state';

/**
 * One string field off the joined site's `bv_extn`.
 *
 * Blank collapses to null on purpose: `resolveChargeMap` reads null as "not
 * distributor-scoped" and skips its distributor tiers, whereas an empty string
 * would be compared against real distributor names and match nothing while
 * still looking like a supplied discriminator.
 */
export function siteExtnText(
  extn: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const v = extn?.[key];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * The charge map plus the site facts its resolution needs, or `undefined`.
 *
 * `undefined`, never `{ rows: [], ... }`: `pairLines` branches on whether a map
 * was supplied at all. Given one, an invoice line it cannot find is reported
 * `unmapped` and pairs with nothing, so an empty map (a retailer whose sheet has
 * not been seeded, or a load still in flight) would mark EVERY charge on the
 * invoice unrecognised and empty the retailer side of the variance.
 */
export function buildMapContext(
  rows: ChargeMapRow[],
  siteExtn: Record<string, unknown> | null | undefined,
  actualLines: ReadonlyArray<{ description: string; section: string }>,
  distributorAliases?: NameAliases,
): MapContext | undefined {
  if (rows.length === 0) return undefined;
  return {
    rows,
    distributor: siteExtnText(siteExtn, 'network_distributor'),
    tariff: siteExtnText(siteExtn, 'network_tariff_code'),
    distributorAliases,
    // A name this invoice prints in two sections stays in its own section.
    strictItems: itemsInSeveralSections(actualLines),
  };
}

/**
 * Every charge as a Comparison row, grouped under the section it is COMPARED in.
 *
 * `sourceIndex` is stamped before the grouping, because grouping by section is
 * what destroys the array position, and an inline edit (and the report's line
 * listing) has to know which line of `result.lines` a row came from.
 *
 * Invoice lines are bucketed by where the MAP says the charge belongs, not by
 * the section the retailer printed it under (`actualSectionFor`), then paired
 * per section, then `applyCanonicalSections` runs last so it wins: Supply is
 * always metering, even if the map says otherwise. Pairing must happen first
 * for that to work at all, since an invoice line called "Service Charge" is only
 * recognisable as Supply once the charge map has renamed it.
 */
export function comparisonRowsBySection(
  sr: ShadowBillResult,
  ar: ActualBillResult | null,
  mapContext?: MapContext,
): Partial<Record<ShadowBillSection, ChargeRow[]>> {
  const shadowBySection: Partial<Record<ShadowBillSection, ComparableLine[]>> = {};
  sr.lines.forEach((l, sourceIndex) => {
    (shadowBySection[l.section] ??= []).push({ ...l, sourceIndex });
  });

  const actualBySection: Partial<Record<ShadowBillSection, ComparableLine[]>> = {};
  ar?.lines.forEach((l, sourceIndex) => {
    const s = actualSectionFor(l.description, l.section as ShadowBillSection, mapContext);
    (actualBySection[s] ??= []).push({ ...l, sourceIndex });
  });

  const paired = COMPARED_SECTIONS.flatMap((key) =>
    pairLines(shadowBySection[key] ?? [], actualBySection[key] ?? [], key, mapContext),
  );
  const out: Partial<Record<ShadowBillSection, ChargeRow[]>> = {};
  for (const r of applyCanonicalSections(paired)) (out[r.section] ??= []).push(r);
  return out;
}

export type ReportSectionRow = {
  key: ShadowBillSection | 'not_compared';
  label: string;
  shadow: number;
  actual: number;
};

export type ReportComparison = {
  /** The section rows the report prints, in order. */
  sections: ReportSectionRow[];
  /** Every row, after the canonical-section pass — for the TOU and line listings. */
  rows: ChargeRow[];
  totals: ComparisonTotals;
  /** The section each retailer line is compared in, by its index in `ar.lines`. */
  actualLineSection: Map<number, ShadowBillSection>;
};

/** Charge-map pass-throughs (VALIDATION POSSIBLE = N). Unmapped charges are
 *  counted in their section since 2026-10-07, so they are not in this row. */
export const NOT_COMPARED_LABEL = 'Pass-through (charge map, not validated)';

/**
 * The report's section table, built from the Comparison rows.
 *
 * Section figures are `comparisonTotals`, exactly what the Comparison tab
 * totals. Environmental shows only for a GreenPower site, and a section that is
 * $0 on both sides is dropped, the same rules `visibleSections` applied.
 *
 * Unmapped and passthrough charges have no expected figure, so the Comparison
 * tab leaves them out of every section's variance and states them separately.
 * The report does the same with one "Not compared" row. Without it the section
 * rows would stop adding up to the retailer's subtotal beneath them, and the
 * reader would have no way to see why.
 */
export function reportComparison(
  sr: ShadowBillResult,
  ar: ActualBillResult,
  exclusions: CalcExclusion[],
  hasGreenPower: boolean,
  mapContext?: MapContext,
): ReportComparison {
  const bySection = comparisonRowsBySection(sr, ar, mapContext);
  const compared = COMPARED_SECTIONS.filter((k) => k !== 'environmental' || hasGreenPower);
  const rows = compared.flatMap((k) => bySection[k] ?? []);
  const totals = comparisonTotals(rows, exclusions, ar.gst);
  const gap = excludedFromVariance(rows, exclusions);

  const sections: ReportSectionRow[] = REPORT_SECTIONS.flatMap(({ key, label }) => {
    if (!compared.includes(key)) return [];
    const { expected, retailer } = totals.sections[key];
    if (expected === 0 && retailer === 0) return [];
    return [{ key, label, shadow: expected, actual: retailer }];
  });
  if (gap.retailer !== 0) {
    sections.push({ key: 'not_compared', label: NOT_COMPARED_LABEL, shadow: 0, actual: gap.retailer });
  }

  const actualLineSection = new Map<number, ShadowBillSection>();
  for (const r of COMPARED_SECTIONS.flatMap((k) => bySection[k] ?? [])) {
    for (const l of r.actual) if (l.sourceIndex != null) actualLineSection.set(l.sourceIndex, r.section);
  }

  return { sections, rows, totals, actualLineSection };
}

/**
 * The expected and invoiced amounts of the Comparison rows in one section whose
 * charge name matches `pattern`, switched-off rows left out. A side with no
 * matching line is null, not 0: "the retailer printed no such line" is a
 * different statement from "the retailer charged nothing for it".
 */
export function matchRowAmounts(
  rows: ChargeRow[],
  exclusions: CalcExclusion[],
  section: ShadowBillSection,
  pattern: RegExp,
): { expected: number | null; invoiced: number | null } {
  const hits = rows.filter((r) => r.section === section && pattern.test(r.label) && !isRowExcluded(r, exclusions));
  const sum = (side: 'shadow' | 'actual') => {
    const lines = hits.flatMap((r) => r[side]);
    if (lines.length === 0) return null;
    return parseFloat(lines.reduce((s, l) => s + (Number(l.amount) || 0), 0).toFixed(2));
  };
  return { expected: sum('shadow'), invoiced: sum('actual') };
}
